import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, describeAuthRefusal } from '@/lib/api';
import { hasPendingAuthorization } from '@/lib/mcp-consent';
import { SignOutStatus } from '@/components/SignOutStatus';
import { AuthCard } from './AuthCard';

/** Aviso de quem chegou ao login vindo da autorização de um agente do MCP. */
export const RETURN_TO_AUTHORIZATION_NOTICE = 'Depois de entrar, você voltará à autorização do agente.';

interface LoginResponse {
  requiresPasswordChange: boolean;
  requiresMfaEnroll: boolean;
  requiresMfa: boolean;
}

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Só lê a chave: quem a consome é a página de consentimento, depois do login completo.
  const [returnsToAuthorization] = useState(hasPendingAuthorization);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post<LoginResponse>('/platform/auth/login', {
        email,
        password,
      });

      // The 4 states (design 5.2): branch based on the intent flags.
      if (data.requiresPasswordChange) {
        navigate('/login/password-change');
      } else if (data.requiresMfaEnroll) {
        navigate('/login/mfa-enroll');
      } else if (data.requiresMfa) {
        navigate('/login/mfa-verify');
      } else {
        // Should not happen at MVP — all real accounts have MFA enabled.
        setError('Estado de login inesperado.');
      }
    } catch (err) {
      // 429 do freio por (email, IP) e do limite por IP: a mensagem diz quanto esperar.
      setError(describeAuthRefusal(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard title="Entrar" subtitle="Acesso restrito a operadores da plataforma.">
      <form onSubmit={onSubmit}>
        <SignOutStatus />
        {error && <div className="error-banner">{error}</div>}
        {returnsToAuthorization && (
          <div className="info-banner" role="status">
            {RETURN_TO_AUTHORIZATION_NOTICE}
          </div>
        )}

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
          />
        </div>

        <div className="field">
          <label htmlFor="password">Senha</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <button type="submit" className="btn" style={{ width: '100%' }} disabled={submitting}>
          {submitting ? 'Entrando…' : 'Continuar'}
        </button>
      </form>
    </AuthCard>
  );
}
